import { useCallback, useEffect, useState } from "react";
import {
  removePushSubscriptionByEndpoint,
  upsertPushSubscription,
} from "../api/nudges";
import {
  getPushPermission,
  isPushSupported,
  isStandaloneDisplay,
  subscribeToPush,
  unsubscribePush,
} from "../lib/pushNotifications";

export function usePushNotifications({ enabled = true } = {}) {
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refreshPermission = useCallback(async () => {
    const p = await getPushPermission();
    setPermission(p);
    return p;
  }, []);

  useEffect(() => {
    if (!enabled || !isPushSupported()) return;
    let cancelled = false;
    (async () => {
      await refreshPermission();
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = await reg?.pushManager?.getSubscription();
        if (!cancelled) setSubscribed(Boolean(sub));
      } catch {
        if (!cancelled) setSubscribed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, refreshPermission]);

  const enablePush = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const subscription = await subscribeToPush();
      if (subscription) {
        await upsertPushSubscription(subscription);
        setSubscribed(true);
      }
      await refreshPermission();
    } catch (e) {
      setSubscribed(false);
      setError(e instanceof Error ? e.message : String(e));
      throw e;
    } finally {
      setLoading(false);
    }
  }, [refreshPermission]);

  const disablePush = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const endpoint = await unsubscribePush();
      if (endpoint) {
        await removePushSubscriptionByEndpoint(endpoint);
      }
      setSubscribed(false);
      await refreshPermission();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      throw e;
    } finally {
      setLoading(false);
    }
  }, [refreshPermission]);

  return {
    supported: isPushSupported(),
    standalone: isStandaloneDisplay(),
    permission,
    subscribed,
    loading,
    error,
    enablePush,
    disablePush,
    refreshPermission,
  };
}
